import { Divider } from '@advanza/ui'
import LoadingDots from '@trustoo/components/ui/LoadingDots'
import clsx from 'clsx'
import Card from 'components/Card'
import {
    formatChurnDriver,
    HEALTH_SCORE_WIDGET_ID,
    SEGMENT_BADGE_CLASSES,
    SEGMENT_LABEL,
    URGENCY_LABEL,
    URGENCY_TAG_CLASSES,
} from './healthScoreConstants'
import SignalRow from './SignalRow'
import type { HealthScore, HealthScoreSegment, HealthScoreSignal } from './types'
import { useHealthScore } from './useHealthScore'

interface HealthScoreWidgetProps {
    providerId: number
}

const HealthScoreWidget = ({ providerId }: HealthScoreWidgetProps) => {
    const { data, isLoading, error } = useHealthScore(providerId)

    const content = (() => {
        if (isLoading) {
            return <LoadingDots />
        }
        if (error) {
            return <p className="text-md text-neutral-500">Could not load health score.</p>
        }
        if (data) {
            return <HealthScoreBody data={data} />
        }
        return <p className="text-md text-neutral-500">Not yet available</p>
    })()

    return (
        <Card
            id={HEALTH_SCORE_WIDGET_ID}
            icon="vital_signs"
            header="Health score"
            topRight={data && <ScoreBadge segment={data.segment} score={data.health_score} />}>
            <Divider m />
            {content}
        </Card>
    )
}

interface ScoreBadgeProps {
    segment: HealthScoreSegment
    score: number
}

const ScoreBadge = ({ segment, score }: ScoreBadgeProps) => (
    <div className="flex items-center gap-2">
        <span
            className={clsx(
                'inline-flex items-center px-2 py-0.5 rounded-full text-sm font-semibold',
                SEGMENT_BADGE_CLASSES[segment],
            )}>
            {SEGMENT_LABEL[segment]}
        </span>
        <div className="text-right leading-tight">
            <div className="text-2xl font-semibold text-neutral-900">{score.toFixed(1)}</div>
            <div className="text-[10px] text-neutral-400 uppercase tracking-wide">health score</div>
        </div>
    </div>
)

interface HealthScoreBodyProps {
    data: HealthScore
}

const HealthScoreBody = ({ data }: HealthScoreBodyProps) => {
    const signals = resolveSignals(data)
    const suggestions = splitPipeText(data.cs_suggestions)

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-md">
                <span
                    className={clsx(
                        'inline-flex items-center px-2 py-0.5 rounded-full text-sm font-medium border',
                        URGENCY_TAG_CLASSES[data.urgency],
                    )}>
                    {URGENCY_LABEL[data.urgency]}
                </span>
                <span className="text-neutral-500">Primary driver:</span>
                <span className="font-semibold text-neutral-900">
                    {formatChurnDriver(data.churn_driver)}
                </span>
                {data.win_rate_delta_display && (
                    <DeltaIndicator label={data.win_rate_delta_display} />
                )}
                {data.response_rate_delta_display && (
                    <DeltaIndicator label={data.response_rate_delta_display} />
                )}
            </div>

            {signals.length > 0 && (
                <div className="border-t border-neutral-100">
                    {signals.map((signal, i) => (
                        <SignalRow key={i} signal={signal} />
                    ))}
                </div>
            )}

            {suggestions.length > 0 && (
                <div className="space-y-2">
                    <h6 className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
                        Next actions
                    </h6>
                    {suggestions.map((suggestion, i) => (
                        <div
                            key={i}
                            className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-3 text-md leading-relaxed">
                            {suggestion}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

interface DeltaIndicatorProps {
    label: string
}

const DeltaIndicator = ({ label }: DeltaIndicatorProps) => {
    const isImprovement = label.startsWith('↑')
    return (
        <span
            className={clsx(
                'text-md font-medium whitespace-nowrap',
                isImprovement ? 'text-green-700' : 'text-red-600',
            )}>
            {label}
        </span>
    )
}

const splitPipeText = (value: string | null | undefined): string[] => {
    if (!value) {
        return []
    }

    return value
        .split('|')
        .map((part) => part.trim())
        .filter(Boolean)
}

const resolveSignals = (data: HealthScore): HealthScoreSignal[] => {
    if (data.signals_enriched && data.signals_enriched.length > 0) {
        return data.signals_enriched
    }

    return splitPipeText(data.risk_drivers).map((text) => ({
        text,
        severity: data.segment,
        stat: '',
        substat: '',
    }))
}

export default HealthScoreWidget
